import { useState } from "react";
import { logoRow } from "@/lib/svg";
import {
  ArrowRight,
  ShieldCheck,
  Smartphone,
  Landmark,
  Building2,
  Wallet,
  LineChart,
  Clock,
  Lock,
  Users,
  PhoneCall,
  MapPin,
  Menu,
  X,
  Check,
} from "lucide-react";

const HERO = "https://images.pexels.com/photos/32804147/pexels-photo-32804147.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const IMG_PERSONAL = "https://images.unsplash.com/photo-1791356777497-2748bb907a1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const IMG_BUSINESS = "https://images.unsplash.com/photo-1753164726626-e4c38056a03f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const IMG_WEALTH = "https://images.pexels.com/photos/7339388/pexels-photo-7339388.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=900";

const NAV = [
  ["#personal", "Personal"],
  ["#business", "Business"],
  ["#wealth", "Wealth"],
  ["#why", "Why Summit Ridge"],
];

const PRODUCTS = [
  {
    id: "personal",
    tag: "Personal",
    img: IMG_PERSONAL,
    title: "Everyday banking that stays out of your way",
    copy: "Open a Premier Checking account in about eight minutes and move your direct deposit over with our switch kit. You'll see paychecks up to two days early, earn interest on every balance tier, and never pay a maintenance fee or overdraft surcharge. Your debit card, Apple Pay and Google Pay are active the moment you're approved.",
    highlight: { value: "4.15%", label: "APY on High-Yield Savings" },
    points: [
      "Premier Checking at 0.35% APY, no minimum balance",
      "Early direct deposit, up to 2 days ahead",
      "Fee-free access at 55,000+ Allpoint ATMs",
      "Real-time card freeze, travel notices and alerts",
      "Round-up savings that sweep spare change automatically",
      "Joint accounts and teen cards with parental controls",
    ],
    note: "FDIC-style deposit protection to the maximum permitted. No monthly fees, ever.",
  },
  {
    id: "business",
    tag: "Business",
    img: IMG_BUSINESS,
    title: "Treasury built for the way you operate",
    copy: "Run operating, payroll, vendor and reserve accounts under one login with a shared routing number. Originate same-day ACH batches and domestic wires before the 4pm MT cutoff, require dual approval on anything leaving the business, and give your bookkeeper view-only access without sharing passwords. Every entry posts to a real-time ledger you can export in one click.",
    highlight: { value: "4pm MT", label: "Same-day wire & ACH cutoff" },
    points: [
      "Unlimited sub-accounts with a single routing number",
      "Same-day ACH origination and domestic wires",
      "Dual-approval controls on outbound payments",
      "Role-based access for staff, bookkeepers and CPAs",
      "Positive Pay and ACH debit blocks for fraud control",
      "Lines of credit and SBA-backed lending on request",
    ],
    note: "A named commercial banker is assigned to every business relationship.",
  },
  {
    id: "wealth",
    tag: "Wealth",
    img: IMG_WEALTH,
    title: "Advice that compounds, generation to generation",
    copy: "Work with a fee-only fiduciary who builds a written plan around your goals, not a product shelf. We coordinate trust and estate administration with your attorney, manage diversified portfolios with quarterly reviews, and arrange private lending against securities or property when liquidity matters. Your advisor lives in the same mountain towns you do.",
    highlight: { value: "$0", label: "Commission — fee-only advice" },
    points: [
      "Written, goal-based financial plans",
      "Trust and estate administration with your attorney",
      "Discretionary portfolio management, reviewed quarterly",
      "Tax-aware rebalancing and charitable gifting strategy",
      "Private lending against securities or real estate",
      "Family-office style coordination across generations",
    ],
    note: "Fiduciary standard on every recommendation — your interests first.",
  },
];

const FEATURES = [
  [ShieldCheck, "Deposits protected", "Every deposit account is insured to the maximum limit permitted under federal law."],
  [Smartphone, "Mobile-first", "Deposit checks, move money and freeze cards in seconds from iOS and Android."],
  [Clock, "Real-time ledger", "Balances and statements update the moment a transaction posts — no overnight lag."],
  [Lock, "Bank-grade security", "Passkeys, device binding and 24/7 anomaly detection on every login."],
  [Users, "Local bankers", "A named banker for every business and wealth relationship, based in your region."],
  [LineChart, "Clear reporting", "Export statements and transactions to CSV for your accountant in one click."],
];

const STATS = [
  ["1998", "Serving the Rockies since"],
  ["38", "Branches across 4 states"],
  ["$6.2B", "Deposits under care"],
  ["4.9/5", "Average client rating"],
];

export default function Home({ onSignIn }) {
  const [open, setOpen] = useState(false);
  const year = new Date().getFullYear();

  return (
    <div className="mkt" data-testid="home-view">
      {/* ---- top bar ---- */}
      <header className="mkt-nav">
        <div className="mkt-wrap mkt-nav-in">
          <div className="logo-row mkt-brand" dangerouslySetInnerHTML={{ __html: logoRow(0.92) }} />
          <nav className="mkt-links">
            {NAV.map(([href, label]) => (
              <a key={href} href={href}>{label}</a>
            ))}
          </nav>
          <div className="mkt-nav-cta">
            <button className="btn btn-lime btn-sm" data-testid="home-login-button" onClick={onSignIn}>
              Online banking <ArrowRight size={15} style={{ marginBottom: -2 }} />
            </button>
            <button className="mkt-burger" aria-label="Menu" onClick={() => setOpen((o) => !o)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="mkt-mobile">
            {NAV.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setOpen(false)}>{label}</a>
            ))}
          </div>
        )}
      </header>

      {/* ---- hero ---- */}
      <section className="mkt-hero">
        <img src={HERO} alt="Mist over a mountain lake" className="mkt-hero-img" />
        <div className="mkt-hero-scrim" />
        <div className="mkt-wrap mkt-hero-in">
          <span className="mkt-eyebrow">Personal · Business · Wealth</span>
          <h1 className="mkt-h1">Banking rooted in the mountains, built for what's next.</h1>
          <p className="mkt-lead">
            Summit Ridge Bank pairs the service of a community lender with the tools of a modern
            platform — real-time balances, same-day payments, and bankers who know your name.
          </p>
          <div className="mkt-hero-btns">
            <button className="btn btn-lime" onClick={onSignIn} data-testid="home-hero-login">
              Sign in to online banking <ArrowRight size={16} style={{ marginBottom: -3 }} />
            </button>
            <a className="btn btn-ghost" href="#personal">Explore accounts</a>
          </div>
          <div className="mkt-hero-trust">
            <ShieldCheck size={16} /> Deposits insured to the maximum permitted · Equal Housing Lender
          </div>
        </div>
      </section>

      {/* ---- stats ---- */}
      <section className="mkt-stats mkt-wrap">
        {STATS.map(([v, l]) => (
          <div className="mkt-stat" key={l}>
            <div className="mkt-stat-v">{v}</div>
            <div className="mkt-stat-l">{l}</div>
          </div>
        ))}
      </section>

      {/* ---- products ---- */}
      <section className="mkt-wrap mkt-section">
        <div className="mkt-head">
          <span className="mkt-eyebrow">What we do</span>
          <h2 className="mkt-h2">Three ways to bank with us</h2>
          <p className="mkt-sub">Choose the relationship that fits where you are — and switch as life and business grow.</p>
        </div>
        <div className="mkt-products">
          {PRODUCTS.map((p) => (
            <article className="mkt-card" id={p.id} key={p.id} data-testid={`home-product-${p.id}`}>
              <div className="mkt-card-img" style={{ backgroundImage: `url(${p.img})` }}>
                <span className="mkt-card-tag">{p.tag}</span>
              </div>
              <div className="mkt-card-body">
                <h3 className="mkt-card-title">{p.title}</h3>
                <div className="mkt-card-highlight">
                  <span className="mkt-card-hl-v">{p.highlight.value}</span>
                  <span className="mkt-card-hl-l">{p.highlight.label}</span>
                </div>
                <p className="mkt-card-copy">{p.copy}</p>
                <ul className="mkt-list">
                  {p.points.map((pt) => (
                    <li key={pt}><Check size={15} /> {pt}</li>
                  ))}
                </ul>
                <p className="mkt-card-note">{p.note}</p>
                <button className="mkt-link-btn" onClick={onSignIn}>
                  Get started <ArrowRight size={15} style={{ marginBottom: -2 }} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ---- why / features ---- */}
      <section className="mkt-why" id="why">
        <div className="mkt-wrap mkt-section">
          <div className="mkt-head">
            <span className="mkt-eyebrow">Why Summit Ridge</span>
            <h2 className="mkt-h2">The polish of a big bank, the judgment of a local one</h2>
          </div>
          <div className="mkt-features">
            {FEATURES.map(([Icon, title, copy]) => (
              <div className="mkt-feature" key={title}>
                <div className="mkt-feature-ic"><Icon size={20} /></div>
                <div>
                  <div className="mkt-feature-t">{title}</div>
                  <div className="mkt-feature-c">{copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- cta band ---- */}
      <section className="mkt-cta">
        <div className="mkt-wrap mkt-cta-in">
          <div>
            <h2 className="mkt-h2" style={{ color: "#fff" }}>Ready when you are.</h2>
            <p className="mkt-sub" style={{ color: "#c6d6e3" }}>
              Open online banking or talk to a banker today — most accounts are ready the same afternoon.
            </p>
          </div>
          <div className="mkt-cta-btns">
            <button className="btn btn-lime" onClick={onSignIn}>Online banking login</button>
            <a className="btn btn-ghost" href="#personal">Compare accounts</a>
          </div>
        </div>
      </section>

      {/* ---- footer ---- */}
      <footer className="mkt-footer">
        <div className="mkt-wrap mkt-foot-grid">
          <div className="mkt-foot-brand">
            <div className="logo-row" dangerouslySetInnerHTML={{ __html: logoRow(0.9) }} />
            <p className="mkt-foot-copy">A Division of Ridge Credit Financial. Banking, lending and wealth services for the communities of the Rocky Mountain West.</p>
            <div className="mkt-foot-contact">
              <span><PhoneCall size={14} /> 1-800-555-0199</span>
              <span><MapPin size={14} /> 220 Aspen Grove Way, Summit, CO</span>
            </div>
          </div>
          <div className="mkt-foot-col">
            <h4><Landmark size={14} /> Personal</h4>
            <button className="mkt-foot-link" onClick={onSignIn}>Checking</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Savings</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Cards</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Mobile app</button>
          </div>
          <div className="mkt-foot-col">
            <h4><Building2 size={14} /> Business</h4>
            <button className="mkt-foot-link" onClick={onSignIn}>Operating accounts</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Payments & wires</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Lending</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Treasury</button>
          </div>
          <div className="mkt-foot-col">
            <h4><Wallet size={14} /> Wealth</h4>
            <button className="mkt-foot-link" onClick={onSignIn}>Planning</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Trust services</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Investments</button>
            <button className="mkt-foot-link" onClick={onSignIn}>Private lending</button>
          </div>
        </div>
        <div className="mkt-wrap mkt-foot-legal">
          <span>© {year} Summit Ridge Bank. All rights reserved.</span>
          <span>Deposits insured to the maximum permitted · Equal Housing Lender</span>
        </div>
      </footer>
    </div>
  );
}
